import { extendTheme } from '@chakra-ui/react';
import {
  spectrumColors,
  logoBrandColors,
  matchClarityColors,
  utkarshColors,
  utkarshRadii,
  PALETTE_IDS,
  PALETTE_STORAGE_KEY,
} from './colors';
import { primaryGradientMotion, secondaryGradientMotion } from './brandMotion';

export function readStoredPaletteId() {
  try {
    let v = localStorage.getItem(PALETTE_STORAGE_KEY);
    // Migrate previous palette id → spectrum
    if (v === 'apsez') v = 'spectrum';
    if (PALETTE_IDS.includes(v)) return v;
  } catch {
    /* ignore */
  }
  return 'spectrum';
}

export function writeStoredPaletteId(id) {
  try {
    localStorage.setItem(PALETTE_STORAGE_KEY, id);
  } catch {
    /* ignore */
  }
}

function resolveTokens(paletteId) {
  if (paletteId === 'logoBrand') return logoBrandColors;
  if (paletteId === 'matchClarity') return matchClarityColors;
  if (paletteId === 'spectrum') return spectrumColors;
  return utkarshColors;
}

/**
 * Build Chakra theme from classic amber or logo-brand tokens.
 */
export function createUtkarshTheme(paletteId = 'spectrum') {
  const tokens = resolveTokens(paletteId);
  const {
    amber,
    ink,
    bgWarm,
    inputBg,
    border,
    secondaryText,
    placeholderGray,
    amberTint,
    white,
    success,
    error,
    errorSoft,
    errorTint,
    warning,
    info,
    dark,
  } = tokens;

  const onAccent = tokens.onAccent || ink;
  const secondary = tokens.secondary || amber;
  const onSecondary = tokens.onSecondary || onAccent;
  const secondaryTint = tokens.secondaryTint || amberTint;

  const brandScale =
    tokens.brandScale || {
      50: amberTint,
      100: '#FCE7A3',
      200: '#FBD66A',
      300: '#FAC531',
      400: '#F9B101',
      500: amber,
      600: '#D99A00',
      700: '#B88000',
      800: '#966700',
      900: '#744F00',
    };

  const secondaryScale =
    tokens.secondaryScale || {
      50: secondaryTint,
      100: '#FFD9B8',
      200: '#FFC08A',
      300: '#FFA65C',
      400: '#F58C38',
      500: secondary,
      600: '#C96418',
      700: '#A95012',
      800: '#893C0D',
      900: '#6A2C09',
    };

  const focusRing = `0 0 0 1px ${amber}`;

  const fonts = tokens.fonts || {
    heading: 'Inter, system-ui, -apple-system, Segoe UI, Roboto, sans-serif',
    body: 'Inter, system-ui, -apple-system, Segoe UI, Roboto, sans-serif',
  };

  return extendTheme({
    config: {
      initialColorMode: 'light',
      useSystemColorMode: false,
      storageKey: 'utkarsh-color-mode',
    },
    fonts: {
      heading: fonts.heading,
      body: fonts.body,
    },
    radii: {
      sm: utkarshRadii.sm,
      md: utkarshRadii.md,
      lg: utkarshRadii.lg,
      full: utkarshRadii.pill,
    },
    colors: {
      utkarsh: {
        amber,
        ink,
        bgWarm,
        inputBg,
        border,
        secondaryText,
        placeholderGray,
        amberTint,
        white,
        onAccent,
        secondary,
        onSecondary,
        secondaryTint,
        success,
        error,
        errorSoft,
        warning,
        info,
        royal: tokens.royal || ink,
        cyan: tokens.cyan || info,
        orange: tokens.orange || warning,
        magenta: tokens.magenta || error,
        navy: tokens.navy || ink,
        darkBg: dark.bg,
        darkSurface: dark.surface,
        darkSurfaceElevated: dark.surfaceElevated,
        darkInputBg: dark.inputBg,
        darkBorder: dark.border,
        darkText: dark.text,
        darkSecondaryText: dark.secondaryText,
        darkPlaceholder: dark.placeholder,
        darkAmberTint: dark.amberTint,
        darkErrorTint: dark.errorTint,
        errorTint,
      },
      brand: brandScale,
      brandSecondary: secondaryScale,
    },
    semanticTokens: {
      colors: {
        'bg.canvas': { default: bgWarm, _dark: dark.bg },
        'bg.surface': { default: white, _dark: dark.surface },
        'bg.elevated': { default: white, _dark: dark.surfaceElevated },
        'bg.input': { default: inputBg, _dark: dark.inputBg },
        'bg.amberTint': { default: amberTint, _dark: dark.amberTint },
        'bg.secondaryTint': { default: secondaryTint, _dark: dark.amberTint },
        'bg.errorTint': { default: errorTint, _dark: dark.errorTint },
        'bg.sidebar': { default: ink, _dark: dark.surfaceElevated },
        'border.subtle': { default: border, _dark: dark.border },
        'text.primary': { default: ink, _dark: dark.text },
        'text.secondary': { default: secondaryText, _dark: dark.secondaryText },
        'text.placeholder': { default: placeholderGray, _dark: dark.placeholder },
        'text.onAmber': { default: onAccent, _dark: onAccent },
        'text.onSecondary': { default: onSecondary, _dark: onSecondary },
        'accent.amber': { default: amber, _dark: amber },
        'accent.primary': { default: amber, _dark: amber },
        'accent.secondary': { default: secondary, _dark: secondary },
        'status.success': { default: success, _dark: '#66BB6A' },
        'status.error': { default: error, _dark: errorSoft },
        'status.warning': { default: warning, _dark: '#FFB74D' },
        'status.info': { default: info, _dark: '#4DB6AC' },
      },
    },
    styles: {
      global: (props) => ({
        body: {
          bg: props.colorMode === 'dark' ? dark.bg : bgWarm,
          color: props.colorMode === 'dark' ? dark.text : ink,
          fontFamily: fonts.body,
        },
      }),
    },
    components: {
      Button: {
        baseStyle: {
          fontWeight: '600',
          borderRadius: 'md',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          cursor: 'pointer',
          '> *': {
            pointerEvents: 'none',
          },
        },
        sizes: {
          md: {
            fontSize: '16px',
            px: 5,
            py: 0,
            h: '52px',
            minH: '52px',
            lineHeight: '1.2',
          },
          sm: {
            fontSize: '14px',
            px: 4,
            py: 0,
            h: '40px',
            minH: '40px',
            lineHeight: '1.2',
          },
          xs: {
            fontSize: '13px',
            px: 3,
            py: 0,
            h: '32px',
            minH: '32px',
            lineHeight: '1.2',
          },
        },
        variants: {
          primary: tokens.buttonGradient
            ? {
                // Gradient string on both — never `bg: transparent` (wipes the fill).
                bg: tokens.buttonGradient,
                backgroundImage: tokens.buttonGradient,
                color: 'text.onAmber',
                border: 'none',
                ...primaryGradientMotion('10s'),
                _hover: {
                  opacity: 0.92,
                  bg: tokens.buttonGradient,
                  backgroundImage: tokens.buttonGradient,
                  _disabled: {
                    opacity: 0.5,
                    bg: tokens.buttonGradient,
                    backgroundImage: tokens.buttonGradient,
                  },
                },
                _active: {
                  opacity: 0.88,
                  bg: tokens.buttonGradient,
                  backgroundImage: tokens.buttonGradient,
                },
                _disabled: {
                  opacity: 0.5,
                  cursor: 'not-allowed',
                  animation: 'none',
                  backgroundSize: '100% 100%',
                },
              }
            : {
                bg: 'utkarsh.amber',
                color: 'text.onAmber',
                _hover: { bg: 'brand.600', _disabled: { bg: 'utkarsh.amber' } },
                _active: { bg: 'brand.700' },
                _disabled: { opacity: 0.5, cursor: 'not-allowed' },
              },
          secondary: tokens.secondaryGradient
            ? {
                bg: tokens.secondaryGradient,
                backgroundImage: tokens.secondaryGradient,
                color: 'text.onSecondary',
                border: 'none',
                ...secondaryGradientMotion('12s'),
                _hover: {
                  opacity: 0.92,
                  bg: tokens.secondaryGradient,
                  backgroundImage: tokens.secondaryGradient,
                  _disabled: {
                    opacity: 0.5,
                    bg: tokens.secondaryGradient,
                    backgroundImage: tokens.secondaryGradient,
                  },
                },
                _active: {
                  opacity: 0.88,
                  bg: tokens.secondaryGradient,
                  backgroundImage: tokens.secondaryGradient,
                },
                _disabled: {
                  opacity: 0.5,
                  cursor: 'not-allowed',
                  animation: 'none',
                  backgroundSize: '100% 100%',
                },
              }
            : {
                bg: 'utkarsh.secondary',
                color: 'text.onSecondary',
                _hover: {
                  bg: 'brandSecondary.600',
                  _disabled: { bg: 'utkarsh.secondary' },
                },
                _active: { bg: 'brandSecondary.700' },
                _disabled: { opacity: 0.5, cursor: 'not-allowed' },
              },
          outlineInk: {
            bg: 'transparent',
            color: 'text.primary',
            border: '1px solid',
            borderColor: 'border.subtle',
            _hover: { bg: 'bg.input' },
          },
          ghostInk: {
            bg: 'transparent',
            color: 'text.primary',
            _hover: { bg: 'bg.input' },
          },
          danger: {
            bg: 'transparent',
            color: 'status.error',
            border: '1px solid',
            borderColor: 'border.subtle',
            _hover: { bg: 'red.50', _dark: { bg: 'whiteAlpha.100' } },
          },
        },
        defaultProps: {
          variant: 'primary',
          size: 'md',
        },
      },
      IconButton: {
        baseStyle: {
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          '> *': {
            pointerEvents: 'none',
          },
        },
      },
      Input: {
        variants: {
          filled: {
            field: {
              bg: 'bg.input',
              border: '1px solid',
              borderColor: 'border.subtle',
              borderRadius: 'sm',
              color: 'text.primary',
              _placeholder: { color: 'text.placeholder' },
              _hover: { bg: 'bg.input' },
              _focus: {
                bg: 'bg.input',
                borderColor: 'utkarsh.amber',
                boxShadow: focusRing,
              },
            },
          },
        },
        defaultProps: {
          variant: 'filled',
        },
      },
      Select: {
        variants: {
          filled: {
            field: {
              bg: 'bg.input',
              border: '1px solid',
              borderColor: 'border.subtle',
              borderRadius: 'sm',
              color: 'text.primary',
              _hover: { bg: 'bg.input' },
              _focus: {
                bg: 'bg.input',
                borderColor: 'utkarsh.amber',
                boxShadow: focusRing,
              },
            },
          },
        },
        defaultProps: {
          variant: 'filled',
        },
      },
      Textarea: {
        variants: {
          filled: {
            bg: 'bg.input',
            border: '1px solid',
            borderColor: 'border.subtle',
            borderRadius: 'sm',
            color: 'text.primary',
            _placeholder: { color: 'text.placeholder' },
            _focus: {
              bg: 'bg.input',
              borderColor: 'utkarsh.amber',
              boxShadow: focusRing,
            },
          },
        },
        defaultProps: {
          variant: 'filled',
        },
      },
      FormLabel: {
        baseStyle: {
          fontSize: '14px',
          fontWeight: '600',
          color: 'text.primary',
          mb: 2,
        },
      },
      Heading: {
        baseStyle: {
          color: 'text.primary',
          fontWeight: '700',
        },
      },
      Badge: {
        baseStyle: {
          borderRadius: 'full',
          px: 3,
          py: 1,
          fontWeight: '600',
          textTransform: 'none',
        },
      },
      Card: {
        baseStyle: {
          container: {
            bg: 'bg.surface',
            borderRadius: 'md',
            borderWidth: '1px',
            borderColor: 'border.subtle',
            boxShadow: 'none',
          },
        },
      },
      Table: {
        variants: {
          simple: {
            th: {
              borderColor: 'border.subtle',
              color: 'text.secondary',
              fontSize: '12px',
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
            },
            td: {
              borderColor: 'border.subtle',
              color: 'text.primary',
            },
          },
        },
      },
      Tabs: {
        variants: {
          enclosed: {
            tablist: {
              borderColor: 'border.subtle',
            },
            tab: {
              color: 'text.secondary',
              _selected: {
                color: 'text.primary',
                bg: 'bg.surface',
                borderColor: 'border.subtle',
                borderBottomColor: 'bg.surface',
                fontWeight: '600',
              },
            },
          },
        },
      },
      Switch: {
        baseStyle: {
          track: {
            _checked: { bg: 'utkarsh.amber' },
          },
        },
      },
      Checkbox: {
        baseStyle: {
          control: {
            borderColor: 'border.subtle',
            _checked: {
              bg: 'utkarsh.amber',
              borderColor: 'utkarsh.amber',
              color: 'text.onAmber',
            },
          },
        },
      },
    },
  });
}

const theme = createUtkarshTheme('classic');
export default theme;
